<template>
  <el-dialog
    :visible.sync="visible"
    title="添加处理人员"
    width="650px"
    custom-class="repair-master-dialog"
    @close="handleClose"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="86px" class="repair-master-form">
      <el-form-item label="处理人员" prop="staffIds">
        <div class="repair-master-picker">
          <el-input
            :value="selectedStaffNames"
            readonly
            placeholder="请选择"
            class="repair-master-input"
            @focus="openStaffDialog"
          />
          <el-button class="repair-master-pick-btn" @click="openStaffDialog">选择</el-button>
        </div>
      </el-form-item>
      <el-form-item label="说明" prop="remark">
        <el-input v-model="form.remark" placeholder="请输入说明（选填）" maxlength="100" show-word-limit />
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button class="repair-dialog-btn-cancel" @click="visible = false">取消</el-button>
      <el-button class="repair-dialog-btn-confirm" type="primary" @click="handleSubmit">确定</el-button>
    </span>

    <!-- 选人内嵌弹窗 -->
    <el-dialog
      :visible.sync="staffDialogVisible"
      title="选择处理人员"
      width="760px"
      custom-class="repair-staff-dialog"
      append-to-body
      @open="syncStaffSelection"
    >
      <div class="repair-staff-toolbar">
        <el-input v-model="staffKeyword" clearable size="small" placeholder="请输入" class="repair-staff-search" />
      </div>
      <el-table
        ref="staffTable"
        v-loading="loading"
        :data="filteredStaffOptions"
        row-key="userId"
        style="width: 100%"
        max-height="400"
        :header-cell-style="staffHeaderCellStyle"
        :cell-style="staffCellStyle"
        @selection-change="handleStaffSelectionChange"
      >
        <el-table-column type="selection" width="52" :selectable="checkSelectable" />
        <el-table-column prop="userId" label="员工ID" min-width="150" show-overflow-tooltip />
        <el-table-column label="姓名" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope">{{ formatStaffName(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="tel" label="手机号" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.tel || '-' }}</template>
        </el-table-column>
        <el-table-column prop="orgName" label="组织" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.orgName || '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" min-width="90" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="existingHandlerIds.includes(scope.row.userId)" style="color: #999;">已添加</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button class="repair-dialog-btn-cancel" @click="staffDialogVisible = false">取消</el-button>
        <el-button class="repair-dialog-btn-confirm" type="primary" @click="confirmStaffSelection">确定</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getStaffInfos, listStaffCommunity } from '@/api/work/repairTypeUserApi'
import { getCommunityId } from '@/api/community/communityApi'
import { saveAlarmHandlers, listAlarmHandlers } from '@/api/inspection/alarmSettingApi'

export default {
  name: 'AddAlarmHandler',
  data() {
    return {
      visible: false,
      loading: false,
      staffDialogVisible: false,
      staffKeyword: '',
      staffOptions: [],
      pendingStaffIds: [],
      existingHandlerIds: [], // 已添加的处理人员ID列表
      communityId: '',
      alarmTypeCd: '',
      form: {
        staffIds: [],
        remark: ''
      },
      rules: {
        staffIds: [{ required: true, type: 'array', min: 1, message: '请选择处理人员', trigger: 'change' }]
      }
    }
  },
  computed: {
    selectedStaffNames() {
      return this.staffOptions
        .filter(item => this.form.staffIds.includes(item.userId))
        .map(item => this.formatStaffName(item))
        .join('、')
    },
    filteredStaffOptions() {
      const keyword = this.staffKeyword.trim()
      if (!keyword) return this.staffOptions
      return this.staffOptions.filter(item =>
        [item.userId, item.userName, item.name, item.tel, item.orgName]
          .some(value => String(value || '').includes(keyword))
      )
    }
  },
  methods: {
    async open(params = {}) {
      this.communityId = params.communityId || getCommunityId()
      this.alarmTypeCd = params.alarmTypeCd || ''
      this.form = { staffIds: [], remark: '' }
      this.visible = true
      await Promise.all([
        this.loadStaffOptions(),
        this.loadExistingHandlers()
      ])
    },
    handleClose() {
      this.form = { staffIds: [], remark: '' }
      this.staffOptions = []
      this.pendingStaffIds = []
      this.existingHandlerIds = []
      this.staffKeyword = ''
      this.staffDialogVisible = false
      this.$refs.form && this.$refs.form.clearValidate()
    },
    async loadStaffOptions() {
      this.loading = true
      try {
        console.log('loadStaffOptions - communityId:', this.communityId)

        // 直接通过 getStaffInfos 传入 communityId 获取该小区的员工
        const res = await getStaffInfos({
          page: 1,
          row: 1000,
          communityId: this.communityId
        })
        console.log('getStaffInfos 返回结果:', res)

        const staffs = res.staffs || res.data || []
        console.log('staffs:', staffs)

        this.staffOptions = staffs.map(item => ({
          ...item,
          userId: item.userId || item.staffId,
          userName: item.userName || item.name
        }))

        console.log('最终 staffOptions:', this.staffOptions)
      } catch (error) {
        console.error('loadStaffOptions 错误:', error)
        this.$message.error(error.message || '获取员工列表失败')
      } finally {
        this.loading = false
      }
    },
    async loadExistingHandlers() {
      try {
        const params = {
          page: 1,
          row: 1000,
          communityId: this.communityId,
          alarmTypeCd: this.alarmTypeCd
        }
        const res = await listAlarmHandlers(params)
        this.existingHandlerIds = (res.data || []).map(item => String(item.staffId))
        console.log('已添加的处理人员ID:', this.existingHandlerIds)
      } catch (error) {
        console.error('获取已添加处理人员列表失败:', error)
        this.existingHandlerIds = []
      }
    },
    // 检查行是否可选（已添加的人员不可选）
    checkSelectable(row) {
      return !this.existingHandlerIds.includes(row.userId)
    },
    formatStaffName(staff) {
      return staff.name || staff.userName || staff.userId
    },
    openStaffDialog() {
      this.staffDialogVisible = true
    },
    syncStaffSelection() {
      this.pendingStaffIds = [...this.form.staffIds]
      this.$nextTick(() => {
        if (!this.$refs.staffTable) return
        this.$refs.staffTable.clearSelection()
        this.staffOptions.forEach(item => {
          if (this.pendingStaffIds.includes(item.userId)) {
            this.$refs.staffTable.toggleRowSelection(item, true)
          }
        })
      })
    },
    handleStaffSelectionChange(selection) {
      this.pendingStaffIds = selection.map(item => item.userId)
    },
    confirmStaffSelection() {
      this.form.staffIds = [...this.pendingStaffIds]
      this.staffDialogVisible = false
      this.$refs.form && this.$refs.form.validateField('staffIds')
    },
    staffHeaderCellStyle() {
      return { background: '#EBF2FA', color: '#364153', fontWeight: 500, height: '44px', padding: '0', border: 'none' }
    },
    staffCellStyle() {
      return { color: '#1D2129', height: '48px', padding: '0', border: 'none', borderBottom: '1px solid #F0F4FA' }
    },
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        const selectedStaffs = this.staffOptions.filter(item => this.form.staffIds.includes(item.userId))
        this.loading = true
        try {
          for (const staff of selectedStaffs) {
            await saveAlarmHandlers({
              communityId: this.communityId,
              alarmTypeCd: this.alarmTypeCd,
              staffId: staff.userId,
              staffName: this.formatStaffName(staff),
              remark: this.form.remark || ''
            })
          }
          this.$message.success('添加成功')
          this.$emit('success')
          this.visible = false
        } catch (error) {
          this.$message.error(error.message || '添加失败')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style lang="scss">
.repair-master-dialog {
  border-radius: 12px;
  overflow: hidden;
  .el-dialog__header {
    height: 66px; padding: 0 24px;
    background: #ebf2fa; border-bottom: 1px solid #dce6f5; line-height: 66px;
  }
  .el-dialog__title { color: #1d2129; font-size: 16px; font-weight: 600; }
  .el-dialog__headerbtn { top: 22px; right: 24px; }
  .el-dialog__body { padding: 24px 24px 8px; }
  .el-dialog__footer { padding: 20px 24px 28px; }
}
.repair-master-form {
  .el-form-item { margin-bottom: 20px; }
  .el-form-item__label { color: #1d2129; font-size: 14px; font-weight: 500; }
  .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label::before { color: #fb2c36; }
  .el-input__inner { height: 38px; border-color: #dce6f5; border-radius: 10px; }
  .el-select { width: 100%; }
}
.repair-master-picker { display: flex; width: 100%; gap: 12px; }
.repair-master-input .el-input__inner { cursor: pointer; }
.repair-master-pick-btn {
  width: 82px; height: 38px;
  border-color: #0e76ff; border-radius: 10px; color: #0e76ff; background: #fff;
}
.repair-dialog-btn-cancel,
.repair-dialog-btn-confirm { width: 98px; height: 38px; border-radius: 10px; font-size: 14px; }
.repair-dialog-btn-cancel { border-color: #0e76ff; color: #0e76ff; background: #fff; }
.repair-dialog-btn-confirm { border-color: #0e76ff; background: #0e76ff; color: #fff; }
.repair-staff-dialog {
  border-radius: 12px; overflow: hidden;
  .el-dialog__header {
    height: 66px; padding: 0 24px;
    background: #ebf2fa; border-bottom: 1px solid #dce6f5; line-height: 66px;
  }
  .el-dialog__title { color: #1d2129; font-size: 16px; font-weight: 600; }
  .el-dialog__body { padding: 20px 24px 8px; }
  .el-dialog__footer { padding: 20px 24px 28px; }
  .el-table { border-radius: 8px; overflow: hidden; }
}
.repair-staff-toolbar { display: flex; justify-content: flex-end; margin-bottom: 12px; }
.repair-staff-search .el-input__inner { height: 38px; border-color: #dce6f5; border-radius: 10px; }
</style>
